// src/services/auth/TokenAutoExtend.js
import apiClient from "@/shared/api/apiClient";
import { tokenStorage } from "./tokenStorage";

// Если пользователь неактивен больше 30 минут — не продлеваем
const INACTIVITY_LIMIT_MS = 30 * 60 * 1000;

// Максимальное время удержания сессии при несохранённых изменениях
const MAX_KEEP_ALIVE_MS = 60 * 60 * 1000;

// Минимальная задержка, чтобы не было бесконечного цикла
const MIN_DELAY_MS = 5 * 1000;

// ============================================

class TokenAutoExtend {
  timerId = null;
  running = false;
  lastActivity = Date.now();
  keepAliveHolders = new Set();
  keepAliveSince = null;

  activityHandler = () => {
    this.lastActivity = Date.now();
  };

  start() {
    // Если уже запущен — ничего не делаем
    if (this.running) return;

    // Без access token продлевать нечего
    if (!tokenStorage.accessToken) return;

    this.running = true;
    this.lastActivity = Date.now();
    this.attach();

    this.schedule();
  }

  stop() {
    this.running = false;
    clearTimeout(this.timerId);
    this.timerId = null;
    this.detach();

    console.warn("[TAE] ⛔ Остановлен");
  }

  schedule() {
    if (!this.running) return;

    // Считаем через сколько нужно вызвать check-session
    let delay = tokenStorage.sessionExpiresAt - Date.now();

    // Защита от отрицательных значений
    if (delay < MIN_DELAY_MS) delay = MIN_DELAY_MS;

    clearTimeout(this.timerId);
    this.timerId = setTimeout(() => this.extend(), delay);

    const fireAt = new Date(Date.now() + delay);
    console.log(
      `[TAE] ⏰ Обновляем через ${Math.round(
        delay / 1000
      )} сек (в ${fireAt.toLocaleTimeString("ru-RU")})`
    );
  }

  async extend() {
    if (!this.running) return;

    // Если пользователь долго ничего не делал — не продлеваем
    if (
      !this.hasKeepAlive &&
      Date.now() - this.lastActivity > INACTIVITY_LIMIT_MS
    ) {
      console.warn("[TAE] 💤 Нет активности — продление остановлено");
      return this.stop();
    }

    try {
      const { data } = await apiClient.get("/login/check-session/", {
        metaData: { isNotificationDisabled: true },
      });

      if (!data?.payload?.result) {
        return this.stop();
      }

      const seconds = data?.payload?.session_lifetime_seconds;

      if (!seconds) {
        throw new Error("Сервер не вернул session_lifetime_seconds");
      }

      tokenStorage.updateTTL(seconds / 60);

      console.log(
        `[TAE] ✅ Получили время сессии: ${seconds} сек. Закончится в ${new Date(
          tokenStorage.sessionExpiresAt
        ).toLocaleTimeString("ru-RU")}`
      );

      this.schedule();
    } catch (e) {
      console.error("[TAE] Ошибка при продлении токена", e);

      this.stop();
    }
  }

  attach() {
    ["click", "keydown", "mousemove", "scroll"].forEach((e) =>
      window.addEventListener(e, this.activityHandler)
    );
  }

  detach() {
    ["click", "keydown", "mousemove", "scroll"].forEach((e) =>
      window.removeEventListener(e, this.activityHandler)
    );
  }

  holdKeepAlive(id) {
    if (this.keepAliveHolders.size === 0) {
      this.keepAliveSince = Date.now();
    }
    this.keepAliveHolders.add(id);
  }

  releaseKeepAlive(id) {
    this.keepAliveHolders.delete(id);
    if (this.keepAliveHolders.size === 0) {
      this.keepAliveSince = null;
    }
  }

  get hasKeepAlive() {
    if (this.keepAliveHolders.size === 0) return false;
    if (this.keepAliveSince === null) return false;
    return Date.now() - this.keepAliveSince < MAX_KEEP_ALIVE_MS;
  }
}

export default new TokenAutoExtend();
